import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, ChevronDown, ChevronRight, Search, 
  Tag, Calendar, RefreshCw, Filter, X, TrendingDown, FilterX, Download, SquareCheckBig, Archive, ArrowUpNarrowWide, List, BarChart3,
  ShieldCheck, AlertTriangle, Zap, ListChecks
} from 'lucide-react';
import './DashboardBultos.css';
import './DashboardInventario.css';
import DashboardMenuHeader from '../components/AlmacenMenuHeader';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import { clearCache } from '../utils/dashboardCache';
import FullScreenLoader from './FullScreenLoader';
import GlobalSearch from '../components/GlobalSearch';
import { calculateWorkingDays } from '../utils/timeUtils';
import * as XLSX from 'xlsx';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const filterLabels = {
  IN_PROVEEDOR: "Casas",
  IN_MARCA: "Marca",
  IN_LINEA: "Línea",
  IN_CLASIF2: "Clasificación",
  IN_DESC1: "Producto",
  IN_SEGMENTO: "Portafolio",
  ID_ARTICULO: "SKU ID",
  CO_ALMA: "Cod. Almacén",
  DES_ALMACEN: "Almacén",
  IN_ACTIVO: "Activo (Inv)",
  V_ANULADA: "Anulada (Venta)",
  CLI_INACTIVO: "Inactivo (Cliente)",
  USERNAME: "Vendedor",
  CLI_CANAL: "Canal",
  CLI_PRIN: "Cliente Principal",
  CLI_REG_VERD: "Región Venta",
  CLI_TERRITORIO: "Territorio",
  CLI_SEG_DES: "Segmento Cliente",
};

const parseNumericES = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const s = String(value).trim();
  if (!s) return 0;
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && !hasDot) {
    return Number(s.replace(/\./g, '').replace(',', '.'));
  }
  return Number(s.replace(/,/g, ''));
};

const formatNumber = (num) => {
  if (num === Infinity || num === -Infinity) return 'N/A';
  const n = parseNumericES(num);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

const getDailyVarClass = (val) => parseFloat(val || 0) >= 0 ? 'positive' : 'negative';
const getDailyIcon = (val) => parseFloat(val || 0) >= 0 ? <TrendingUp size={12}/> : <TrendingDown size={12}/>;
const getDailyText = (val) => {
  const v = parseFloat(val || 0);
  return `${Math.round(v)}%`;
};

const getSemaforoColor = (p) => {
  const val = Number(p || 0);
  if (val < 80) return '#8b0000';
  if (val < 90) return '#ff4d4d';
  if (val < 100) return '#ffcc00';
  return '#00ba52';
};

const getStockStatus = (stock, necesidad) => {
  const s = parseNumericES(stock);
  const n = parseNumericES(necesidad);
  if (n <= 0) {
    return { label: 'Abastecido', color: '#00ba52', icon: <ShieldCheck size={14} /> };
  }
  if (s <= 0) {
    return { label: 'Emergencia', color: '#8b0000', icon: <Zap size={14} /> };
  }
  if (s < n) {
    return { label: 'Alerta', color: '#ff8c00', icon: <AlertTriangle size={14} /> };
  }
  return { label: 'Abastecido', color: '#00ba52', icon: <ShieldCheck size={14} /> };
};

const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange, onClearPill }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;
  
  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection 
      ? (selectedValues.length === 1 
          ? (options.find(opt => (opt.value || opt) === selectedValues[0])?.label ?? selectedValues[0])
          : `${selectedValues.length} Selec.`) 
      : 'Todos';
  
  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt => 
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 100);
  
  const areAllSelected = filteredOptions.every(opt => 
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(val => 
        !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      const newValuesOnly = filteredOptions.map(opt => opt.value || opt);
      onChange(name, newValuesOnly);
    }
  };

  const handleSingleToggle = useCallback((optValue) => {
    const newVals = selectedValues.includes(optValue)
      ? selectedValues.filter(v => v !== optValue)
      : [...selectedValues, optValue];
    onChange(name, newVals);
  }, [selectedValues, onChange, name]);

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button 
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input 
                type="text" 
                placeholder="Buscar..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                autoFocus 
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input 
                type="checkbox" 
                checked={areAllSelected} 
                onChange={handleSelectAll} 
              />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input 
                    type="checkbox" 
                    checked={selectedValues.includes(optValue)} 
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button
              className="reset-pill-link"
              onClick={() => onClearPill?.(name)}
            >
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const nombresMeses = ["ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO", "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"];

const INITIAL_FILTERS = {
  IN_PROVEEDOR: [], IN_MARCA: [], IN_LINEA: [],
  IN_CLASIF2: [], IN_DESC1: [], IN_SEGMENTO: [],
  ID_ARTICULO: [], CO_ALMA: [], DES_ALMACEN: [], IN_ACTIVO: [],
  USERNAME: [], CLI_CANAL: [], CLI_PRIN: [], CLI_REG_VERD: [],
  CLI_TERRITORIO: [], CLI_SEG_DES: [],
  V_ANULADA: [],
  CLI_INACTIVO: [],
};

const DashboardInventario = () => {
  const { inventarioQuery, getInventarioFilters, loading: apiLoading } = useQuery(); 
  const { profile } = useAuth();

  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [metrics, setMetrics] = useState({ 
    stock_actual: 0, stock_comprometido: 0, stock_disponible: 0,
    demanda: 0, meta: 0, cumplimiento: 0, var_stock_anual: 0,
    var_diaria_actual: 0,
    var_diaria_comprometido: 0, 
    var_diaria_disp: 0,
    var_diaria_demanda: 0
  });

  const [selectedMeses, setSelectedMeses] = useState([nombresMeses[new Date().getMonth()]]);
  const today = Math.min(new Date().getDate(), 31);
  const [selectedDay, setSelectedDay] = useState([today]);
  const currentYear = new Date().getFullYear();
  const [selectedAnios, setSelectedAnios] = useState([currentYear]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [comparisonMode, setComparisonMode] = useState('CUOTA');
  const [unitMode, setUnitMode] = useState(localStorage.getItem('inventarioUnitMode') || 'bultos');
  
  const requestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const analysisRequestRef = useRef(0);
  const filtersInitialized = useRef(false);
  const [filtersReady, setFiltersReady] = useState(false);
  
  const [workDayMetrics, setWorkDayMetrics] = useState({ total: 0, elapsed: 0, percentage: 0 });
  const [availableYears, setAvailableYears] = useState([]);
  const [, setAvailableDias] = useState([]);
  const [, setAvailableMeses] = useState([]);
  const [activeNavTab, setActiveNavTab] = useState('detalle');
  
  const [detailData, setDetailData] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotalPages, setDetailTotalPages] = useState(1);
  const [detailViewMode, setDetailViewMode] = useState('SIMPLIFICADO');

  const [analysisData, setAnalysisData] = useState([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [expandedAnalysisBrands, setExpandedAnalysisBrands] = useState({});
  const [analysisShowOnlyAlertEmergency, setAnalysisShowOnlyAlertEmergency] = useState(false);
  
  const [disponibilidadData, setDisponibilidadData] = useState([]);
  const [disponibilidadLoading, setDisponibilidadLoading] = useState(false);
  const [dispViewMode, setDispViewMode] = useState('MENSUAL');
  const [dispSelectedMeses, setDispSelectedMeses] = useState(() => {
    return nombresMeses.slice(0, new Date().getMonth() + 1);
  });

  useEffect(() => {
    const metrics = calculateWorkingDays(selectedMeses, selectedAnios, nombresMeses);
    setWorkDayMetrics(metrics);
  }, [selectedMeses, selectedAnios]);

  // Debug: Mostrar filtros actuales
  useEffect(() => {
    console.log('🔍 Filtros actuales:', filters);
    console.log('🔍 Fechas seleccionadas:', {
      meses: selectedMeses,
      anios: selectedAnios,
      dias: selectedDay
    });
    console.log('🔍 Pestaña activa:', activeNavTab);
  }, [filters, selectedMeses, selectedAnios, selectedDay, activeNavTab]);

  // CARGAR CONFIGURACIÓN INICIAL
  useEffect(() => {
    const loadDashboardData = async () => {
      if (filtersInitialized.current) return;
      setLocalLoading(true);
      try {
        const config = await getInventarioFilters();
        
        if (!config) {
          throw new Error("Configuración de inventario no disponible.");
        }
        
        // Asegurar que todos los filtros tengan opciones válidas
        const fullOptions = {};
        Object.keys(INITIAL_FILTERS).forEach(key => {
          fullOptions[key] = Array.isArray(config.availableOptions?.[key]) 
            ? config.availableOptions[key] 
            : [];
        });
        
        setAvailableOptions(fullOptions);
        setAvailableYears(config.availableYears || [currentYear]);
        setAvailableMeses(config.availableMeses || []);
        
        const cleanDias = (config.availableDias || [])
          .map(d => Number(d))
          .filter(Boolean)
          .filter(n => !isNaN(n) && n >= 1 && n <= 31)
          .sort((a,b) => a-b);
        
        setAvailableDias(cleanDias);

        // Filtrar segmentos excluidos
        const allSegments = fullOptions.CLI_SEG_DES || [];
        const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
        const prefilteredSegments = allSegments.filter(segment => 
          !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
        );

        // Configurar filtros por defecto
        const defaultFilters = {
          ...INITIAL_FILTERS,
          CO_ALMA: ['001'],
          V_ANULADA: ['0'],
          CLI_INACTIVO: ['0'],
          CLI_SEG_DES: prefilteredSegments
        };

        setFilters(defaultFilters);
        
        // Configurar fechas
        if (config.currentYear) {
          setSelectedAnios([config.currentYear]);
        } else if (config.availableYears?.[0]) {
          setSelectedAnios([config.availableYears[0]]);
        }
        
        const currentDay = today;
        const availableToday = config.availableDias?.includes(currentDay);
        if (availableToday) {
          setSelectedDay([currentDay]);
        } else if (config.availableDias?.[0]) {
          setSelectedDay([config.availableDias[0]]);
        }
        
        const currentMonth = nombresMeses[new Date().getMonth()];
        const dataMonth = config.availableMeses?.find(m => m === currentMonth);
        setSelectedMeses(dataMonth ? [dataMonth] : (config.availableMeses?.[0] ? [config.availableMeses[0]] : [currentMonth]));
        
        // Marcar como inicializado y listo
        filtersInitialized.current = true;
        setFiltersReady(true);
        console.log('✅ Filtros inicializados correctamente');
        
      } catch (err) {
        console.error('Error al cargar configuración de inventario:', err);
        // En caso de error, igualmente marcamos como listo para no bloquear la UI
        filtersInitialized.current = true;
        setFiltersReady(true);
      } finally {
        setLocalLoading(false);
      }
    };
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FUNCIÓN PARA CARGAR DATOS DE DETALLE
  const fetchDetailData = useCallback(async (page = 1, pageSize = 50) => {
    if (!filtersReady) {
      console.log('⚠️ Filtros no inicializados, no se puede cargar detalle');
      return;
    }
    
    const requestId = ++detailRequestRef.current;
    setDetailLoading(true);
    
    try {
      const payload = {
        metric_type: 'INVENTARIO_DETALLE',
        metric_display_type: unitMode === 'bultos' ? 'BULTOS' : 'UNIDADES',
        page_anios: selectedAnios.filter(a => a),
        page_meses: selectedMeses.filter(m => m),
        page_dias: selectedDay.filter(n => !isNaN(n) && n >= 1 && n <= 31),
        filters: {
          ...filters,
          CO_ALMA: filters.CO_ALMA?.length ? filters.CO_ALMA : ['001'],
          V_ANULADA: filters.V_ANULADA?.length ? filters.V_ANULADA : ['0'],
          CLI_INACTIVO: filters.CLI_INACTIVO?.length ? filters.CLI_INACTIVO : ['0']
        },
        page: page,
        page_size: pageSize,
        user_info: profile ? {
          username: profile.username,
          cargo: profile.cargo
        } : null
      };

      console.log('📊 Enviando payload detalle:', payload);
      const response = await inventarioQuery(payload);
      console.log('📊 Respuesta detalle:', response);
      
      if (requestId === detailRequestRef.current) {
        const data = response.data || response.detalle_inventario || response || [];
        setDetailData(Array.isArray(data) ? data : []);
        setDetailPage(page);
        setDetailTotalPages(response.total_pages || response.totalPages || 1);
      }
      
    } catch (err) {
      if (requestId === detailRequestRef.current) {
        console.error("❌ Error fetchDetailData:", err);
        setDetailData([]);
      }
    } finally {
      if (requestId === detailRequestRef.current) {
        setDetailLoading(false);
      }
    }
  }, [unitMode, selectedMeses, selectedAnios, selectedDay, filters, profile, inventarioQuery, filtersReady]);

  // FUNCIÓN PARA CARGAR DATOS DE ANÁLISIS
  const fetchAnalysisData = useCallback(async () => {
    if (!filtersReady) {
      console.log('⚠️ Filtros no listos para análisis');
      return;
    }
    
    const requestId = ++analysisRequestRef.current;
    setAnalysisLoading(true);
    
    try {
      const payload = {
        metric_type: 'INVENTARIO_ANALISIS',
        metric_display_type: unitMode === 'bultos' ? 'BULTOS' : 'UNIDADES',
        comparison_mode: comparisonMode,
        page_anios: selectedAnios.filter(a => a),
        page_meses: selectedMeses.filter(m => m),
        page_dias: selectedDay.filter(n => !isNaN(n) && n >= 1 && n <= 31),
        filters: {
          ...filters,
          CO_ALMA: filters.CO_ALMA?.length ? filters.CO_ALMA : ['001'],
          V_ANULADA: filters.V_ANULADA?.length ? filters.V_ANULADA : ['0'],
          CLI_INACTIVO: filters.CLI_INACTIVO?.length ? filters.CLI_INACTIVO : ['0']
        },
        user_info: profile ? {
          username: profile.username,
          cargo: profile.cargo
        } : null
      };

      console.log('📊 Enviando payload análisis:', JSON.stringify(payload, null, 2));
      const response = await inventarioQuery(payload);
      console.log('📊 Respuesta análisis:', response);

      let rows = [];
      if (response?.data && Array.isArray(response.data)) {
        rows = response.data;
      } else if (response?.analisis_inventario && Array.isArray(response.analisis_inventario)) {
        rows = response.analisis_inventario;
      } else if (response?.result && Array.isArray(response.result)) {
        rows = response.result;
      } else if (Array.isArray(response)) {
        rows = response;
      }

      if (requestId === analysisRequestRef.current) {
        if (rows.length > 0) {
          const grouped = rows.reduce((acc, curr) => {
            const brand = curr.marca || curr.IN_MARCA || 'SIN MARCA';
            if (!acc[brand]) {
              acc[brand] = {
                marca: brand,
                cuota_forecast: 0, 
                real_venta: 0, 
                inventario: 0, 
                necesidad: 0,
                venta_diaria_3m: 0,
                productos: []
              };
            }
            
            // Manejar diferentes nombres de campos
            const cuotaForecast = Number(curr.cuota_forecast || curr.CUOTA_FORECAST || curr.meta || 0);
            const realVenta = Number(curr.real_venta || curr.REAL_VENTA || curr.venta_real || 0);
            const inventario = Number(curr.inventario || curr.INVENTARIO || curr.stock_disponible || curr.STOCK_DISPONIBLE || 0);
            const necesidad = Number(curr.necesidad || curr.NECESIDAD || curr.demanda || 0);
            const ventaDiaria3m = Number(curr.venta_diaria_3m || curr.VENTA_DIARIA_3M || curr.venta_diaria || 0);
            
            acc[brand].cuota_forecast += cuotaForecast;
            acc[brand].real_venta += realVenta;
            acc[brand].inventario += inventario;
            acc[brand].necesidad += necesidad;
            acc[brand].venta_diaria_3m += ventaDiaria3m;
            
            // Agregar producto individual
            acc[brand].productos.push({
              id_articulo: curr.id_articulo || curr.ID_ARTICULO || '',
              producto: curr.producto || curr.IN_DESC1 || curr.descripcion || 'SIN NOMBRE',
              cuota_forecast: cuotaForecast,
              real_venta: realVenta,
              cumplimiento_pct: cuotaForecast > 0 ? (realVenta / cuotaForecast * 100) : 0,
              inventario: inventario,
              necesidad: necesidad,
              dias_inventario: curr.dias_inventario || curr.DIAS_INVENTARIO || 
                (ventaDiaria3m > 0 ? (inventario / ventaDiaria3m) : 999)
            });
            
            return acc;
          }, {});

          const brandRows = Object.values(grouped);
          const sorted = brandRows.sort((a, b) => b.real_venta - a.real_venta);
          setAnalysisData(sorted);
          console.log('✅ Análisis procesado:', sorted.length, 'marcas');
        } else {
          setAnalysisData([]);
          console.log('⚠️ No hay datos de análisis');
        }
      }
    } catch (err) {
      if (requestId === analysisRequestRef.current) {
        console.error("❌ Error fetchAnalysisData:", err);
        setAnalysisData([]);
      }
    } finally {
      if (requestId === analysisRequestRef.current) {
        setAnalysisLoading(false);
      }
    }
  }, [unitMode, comparisonMode, selectedMeses, selectedAnios, selectedDay, filters, profile, inventarioQuery, filtersReady]);

  // FUNCIÓN PARA CARGAR DISPONIBILIDAD
  const fetchDisponibilidadData = useCallback(async () => {
    if (!filtersReady) return;

    setDisponibilidadLoading(true);

    try {
      const mesesHastaHoy = nombresMeses.slice(0, new Date().getMonth() + 1);

      const mesesParaPayload = dispViewMode === 'SEMANAL' 
        ? (selectedMeses.length > 0 ? selectedMeses : mesesHastaHoy)
        : (dispSelectedMeses.length > 0 ? dispSelectedMeses : mesesHastaHoy);

      const payload = {
        metric_type: 'INVENTARIO_DISPONIBILIDAD',
        metric_display_type: unitMode === 'bultos' ? 'BULTOS' : 'UNIDADES',
        breakdown_type: dispViewMode,
        page_anios: selectedAnios,
        page_meses: mesesParaPayload,
        filters,
        user_info: profile ? {
          username: profile.username,
          cargo: profile.cargo
        } : null
      };

      console.log('📊 DISPONIBILIDAD payload', payload);
      const response = await inventarioQuery(payload);
      console.log('📊 DISPONIBILIDAD response', response);

      setDisponibilidadData(response.data || []);

    } catch (err) {
      console.error('Error disponibilidad', err);
      setDisponibilidadData([]);
    } finally {
      setDisponibilidadLoading(false);
    }

  }, [
    unitMode,
    selectedAnios,
    selectedMeses,
    dispSelectedMeses,
    dispViewMode,
    filters,
    profile,
    inventarioQuery,
    filtersReady
  ]);

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!filtersReady) return;
    
    const requestId = ++requestRef.current;
    setQuerying(true);
    
    try {
      const payload = {
        metric_type: 'INVENTARIO',
        forceRefresh: forceRefresh,
        metric_display_type: unitMode === 'bultos' ? 'BULTOS' : 'UNIDADES',
        comparison_mode: comparisonMode,
        page_anios: selectedAnios.filter(a => a),
        page_meses: selectedMeses.filter(m => m),
        page_dias: selectedDay.filter(n => !isNaN(n) && n >= 1 && n <= 31),
        filters: filters,
        user_info: profile ? {
          username: profile.username,
          cargo: profile.cargo
        } : null
      };

      const rawResponse = await inventarioQuery(payload);
      
      if (requestId !== requestRef.current) {
        return;
      }

      const processed = processResponse(rawResponse);
      setMetrics(processed.metrics);
      
    } catch (err) {
      if (requestId === requestRef.current) {
        console.error("❌ Error fetchData inventario:", err);
      }
    } finally {
      if (requestId === requestRef.current) {
        setQuerying(false);
      }
    }
  }, [comparisonMode, unitMode, selectedMeses, selectedAnios, selectedDay, filters, profile, inventarioQuery, filtersReady]);

  const debouncedFetch = useRef();

  useEffect(() => {
    localStorage.setItem('inventarioUnitMode', unitMode);
  }, [unitMode]);

  // Cambiar de pestaña
  const handleNavTabChange = useCallback((tab) => {
    console.log('🔄 Cambiando a pestaña:', tab);
    setActiveNavTab(tab);

    if (tab === 'detalle') {
      fetchDetailData(1);
    } else if (tab === 'analisis') {
      // Pequeño delay para asegurar que el estado se actualice
      setTimeout(() => {
        fetchAnalysisData();
      }, 100);
    } else if (tab === 'disp') {
      fetchDisponibilidadData();
    }
  }, [fetchDetailData, fetchAnalysisData, fetchDisponibilidadData]);

  // EFECTO PARA CARGAR DETALLE CUANDO SE INICIALIZAN LOS FILTROS
  useEffect(() => {
    if (filtersReady && activeNavTab === 'detalle') {
      fetchDetailData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady]);

  // EFECTO PARA RECARGAR DETALLE CUANDO CAMBIAN FILTROS
  useEffect(() => {
    if (filtersReady && activeNavTab === 'detalle') {
      const timeoutId = setTimeout(() => {
        fetchDetailData(1);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedMeses, selectedAnios, selectedDay, unitMode, filtersReady]);

  // EFECTO PARA RECARGAR ANÁLISIS CUANDO CAMBIAN FILTROS
  useEffect(() => {
    if (filtersReady && activeNavTab === 'analisis') {
      console.log('🔄 Filtros cambiados, recargando análisis...');
      const timeoutId = setTimeout(() => {
        fetchAnalysisData();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedMeses, selectedAnios, selectedDay, unitMode, comparisonMode, filtersReady]);

  // EFECTO PARA CARGAR ANÁLISIS INICIAL
  useEffect(() => {
    if (filtersReady && activeNavTab === 'analisis') {
      console.log('🔄 Cargando análisis inicial...');
      fetchAnalysisData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady, activeNavTab]);

  // Cambiar de vista Mensual/Semanal
  const handleDispViewModeChange = useCallback((newMode) => {
    console.log(`🔄 Cambiando vista a: ${newMode}`);
    setDispViewMode(newMode);
  }, []);

  // useEffect para recargar cuando cambia dispViewMode
  useEffect(() => {
    if (activeNavTab === 'disp' && filtersReady) {
      console.log(`🔄 Recargando disponibilidad en modo: ${dispViewMode}`);
      fetchDisponibilidadData();
    }
  }, [dispViewMode, activeNavTab, fetchDisponibilidadData, filtersReady]);

  // useEffect para recargar vista semanal cuando cambia el mes global
  useEffect(() => {
    if (activeNavTab === 'disp' && dispViewMode === 'SEMANAL' && filtersReady) {
      console.log('🔄 Mes global cambiado, recargando vista semanal');
      fetchDisponibilidadData();
    }
  }, [selectedMeses, activeNavTab, dispViewMode, fetchDisponibilidadData, filtersReady]);

  const toggleAnalysisBrand = (marca) => {
    setExpandedAnalysisBrands(prev => ({ ...prev, [marca]: !prev[marca] }));
  };

  const lastAutoFetchPayloadRef = useRef('');

  useEffect(() => {
    if (!filtersReady) return;

    const stableFilters = Object.keys(filters)
      .sort()
      .reduce((acc, key) => {
        const v = filters[key];
        if (Array.isArray(v) && v.length > 0) acc[key] = v;
        return acc;
      }, {});

    const currentPayloadObj = {
      comparison_mode: comparisonMode,
      unit_mode: unitMode,
      page_anios: selectedAnios,
      page_meses: selectedMeses,
      page_dias: selectedDay,
      filters: stableFilters
    };

    const currentPayload = JSON.stringify(currentPayloadObj);

    if (lastAutoFetchPayloadRef.current === currentPayload) return;

    if (currentPayloadObj.page_anios?.length > 0 || currentPayloadObj.page_meses?.length > 0) {
      if (debouncedFetch.current) {
        clearTimeout(debouncedFetch.current);
      }

      debouncedFetch.current = setTimeout(() => {
        lastAutoFetchPayloadRef.current = currentPayload;
        fetchData();
      }, 300);

      return () => {
        if (debouncedFetch.current) clearTimeout(debouncedFetch.current);
      };
    }
  }, [comparisonMode, unitMode, selectedMeses, selectedAnios, selectedDay, filters, fetchData, filtersReady]);

  const handleResetFilters = useCallback(() => {
    const resetFilters = {
      ...INITIAL_FILTERS,
      CO_ALMA: ['001']
    };
    setFilters(resetFilters);
    console.log('🔄 Filtros reseteados');
  }, []);

  const handleClearSingleFilter = useCallback((name) => {
    setFilters(prev => {
      return { ...prev, [name]: [] };
    });
  }, []);

  const handleClearPill = useCallback((name) => {
    handleClearSingleFilter(name);
  }, [handleClearSingleFilter]);

  const handleRefresh = useCallback(async () => {
    if (debouncedFetch.current) {
      clearTimeout(debouncedFetch.current);
    }
    clearCache();
    setQuerying(true);
    await fetchData(true);
    
    try {
      const config = await getInventarioFilters();
      if (config) {
        const fullOptions = { ...(config.availableOptions || {}) };
        Object.keys(INITIAL_FILTERS).forEach(key => {
          if (!fullOptions[key]) {
            fullOptions[key] = [];
          }
        });
        setAvailableOptions(fullOptions);
        setAvailableMeses(config.availableMeses || []);
        const cleanDias = (config.availableDias || [])
          .map(d => Number(d))
          .filter(Boolean)
          .filter(n => !isNaN(n) && n >= 1 && n <= 31)
          .sort((a,b) => a-b);
        setAvailableDias(cleanDias);
      }
    } catch (e) {
      console.error("Error refreshing config:", e);
    }
  }, [fetchData, getInventarioFilters]);

  const downloadDetailExcel = useCallback((data, unitMode) => {
    if (!data || data.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    const isSimplificado = detailViewMode === 'SIMPLIFICADO';

    const headers = isSimplificado
      ? ['Cod Almacén', 'SKU ID', 'Producto', 'Stock Disp.']
      : [
          'Cod Almacén', 'Almacén', 'Proveedor', 'Marca', 'Línea', 'SKU ID', 
          'Producto', 'Clasif.', 'Stock Disp.', 'Venta Día', 'Fecha'
        ];

    const wsData = [headers];

    data.forEach(row => {
      const suffix = unitMode === 'bultos' ? '_BL' : '_U';

      if (isSimplificado) {
        const stockDisp = row.STOCK_DISPONIBLE ?? row[`STOCK_DISP${suffix}`] ?? 0;
        wsData.push([
          row.CO_ALMA || '',
          row.ID_ARTICULO || '',
          row.IN_DESC1 || '',
          stockDisp
        ]);
        return;
      }

      wsData.push([
        row.CO_ALMA || '',
        row.DES_ALMACEN || '',
        row.IN_PROVEEDOR || '',
        row.IN_MARCA || '',
        row.IN_LINEA || '',
        row.ID_ARTICULO || '',
        row.IN_DESC1 || '',
        row.IN_CLASIF2 || '',
        row.STOCK_ACTUAL || row[`STOCK_ACTUAL${suffix}`] || 0,
        row.STOCK_COMPROMETIDO || row[`STOCK_COMP${suffix}`] || 0,
        row.STOCK_DISPONIBLE || row[`STOCK_DISP${suffix}`] || 0,
        row.VENTA_DIA || row[`VENTA_DIA${suffix}`] || 0,
        row.IN_FECHA || ''
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Inventario Detalle');

    const filename = `Inventario_Detalle_${unitMode}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  }, [detailViewMode]);

  const downloadAnalysisExcel = useCallback((data) => {
    const rows = [];
    data.forEach(brand => {
      rows.push({
        Nivel: 'MARCA', Identificador: brand.marca,
        Meta: Math.round(brand.cuota_forecast), Venta: Math.round(brand.real_venta),
        Cumplimiento: (brand.cuota_forecast > 0 ? (brand.real_venta / brand.cuota_forecast * 100).toFixed(1) : 0) + '%',
        Stock: Math.round(brand.inventario), Necesidad: Math.round(brand.necesidad)
      });
      brand.productos.forEach(p => {
        rows.push({
          Nivel: 'PRODUCTO', Identificador: p.producto,
          Meta: Math.round(p.cuota_forecast), Venta: Math.round(p.real_venta),
          Cumplimiento: p.cumplimiento_pct + '%',
          Stock: Math.round(p.inventario), Necesidad: Math.round(p.necesidad)
        });
      });
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Análisis Inventario');
    XLSX.writeFile(wb, `Analisis_Inventario_${new Date().toISOString().split('T')[0]}.xlsx`);
  }, []);

  const processResponse = (response) => {
    const kpis = response.kpis?.[0] || {};
    
    const stockActual = parseFloat(kpis.stock_actual || 0);
    const stockComprometido = parseFloat(kpis.stock_comprometido || 0);
    const stockDisponible = parseFloat(kpis.stock_disponible || 0);
    const demanda = parseFloat(kpis.demanda || 0);
    
    return {
      metrics: {
        stock_actual: stockActual,
        stock_comprometido: stockComprometido,
        stock_disponible: stockDisponible,
        demanda,
        meta: Number(kpis.meta ?? 0),
        cumplimiento: Number(kpis.cumplimiento_pct ?? 0),
        var_diaria_actual: parseFloat(kpis.var_diaria_actual ?? 0),
        var_diaria_comprometido: parseFloat(kpis.var_diaria_comprometido ?? 0),
        var_diaria_disp: parseFloat(kpis.var_diaria_disponible ?? 0),
        var_diaria_demanda: parseFloat(kpis.var_diaria_demanda ?? 0)
      },
      chartData: [], pieData: [], regionData: []
    };
  };

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <motion.div 
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: "easeInOut" }}
    >
      <DashboardMenuHeader />

      {(apiLoading || localLoading) && !filtersReady ? (
        <FullScreenLoader message="Cargando datos de inventario..." /> 
      ) : null}
      
      {querying && (
        <div className="querying-overlay active" />
      )}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: "easeOut" }}
      >
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Inventario</h1>
            <div className="title-underline"></div>
          </div>
          <div className="date-selectors">
            <button 
              className="refresh-button" 
              onClick={handleRefresh}
              title="Actualizar Datos"
              disabled={querying}
            >
              <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
            </button>
            <button 
              className="mobile-filter-btn" 
              onClick={() => setShowMobileFilters(true)}
            >
              <Filter size={16} />
              <span>Filtros</span>
            </button>
            <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${comparisonMode === 'CUOTA' ? 'active' : ''}`} 
                      onClick={() => setComparisonMode('CUOTA')}>Cuota</button>
              <button className={`switch-toggle-btn ${comparisonMode === 'FORECAST' ? 'active' : ''}`} 
                      onClick={() => setComparisonMode('FORECAST')}>Forecast</button>
            </div>
            <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${unitMode === 'bultos' ? 'active' : ''}`} 
                      onClick={() => setUnitMode('bultos')}>Bultos</button>
              <button className={`switch-toggle-btn ${unitMode === 'unidades' ? 'active' : ''}`} 
                      onClick={() => setUnitMode('unidades')}>Unidades</button>
            </div>
            <div className="date-filters-group">
              <MultiSelectFilter 
                label="Día" 
                name="dias" 
                icon={Calendar} 
                options={Array.from({length: 31}, (_, i) => ({
                        value: i + 1,         
                        label: (i + 1).toString()
                      }))} 
                selectedValues={selectedDay} 
                onChange={(name, vals) => {
                  const validDays = vals
                    .map(v => Number(v))
                    .filter(n => !isNaN(n) && n >= 1 && n <= 31);
                  
                  const newDays = validDays.length > 0 ? [validDays[validDays.length - 1]] : [];
                  setSelectedDay(newDays);
                }} 
              />
              <MultiSelectFilter 
                label="Mes" 
                name="meses" 
                icon={Calendar} 
                options={nombresMeses.map(m => ({value: m, label: m}))} 
                selectedValues={selectedMeses} 
                onChange={(name, vals) => setSelectedMeses(vals.length > 0 ? [vals[vals.length - 1]] : [])} 
              />
              <MultiSelectFilter 
                label="Año" 
                name="anios" 
                icon={Calendar} 
                options={availableYears.map(year => ({value: year, label: year.toString()}))} 
                selectedValues={selectedAnios} 
                onChange={(name, vals) => setSelectedAnios(vals.length > 0 ? [vals[vals.length - 1]] : [])} 
              />
            </div>
            <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
              <GlobalSearch 
                availableOptions={availableOptions}
                filterLabels={filterLabels}
                filters={filters}
                setFilters={(newFilters) => {
                  console.log('🔍 GlobalSearch actualizando filtros:', newFilters);
                  setFilters(newFilters);
                }}
              />
            </div>
          </div>
        </header>

        {(() => {
          const timeVsStockDiff = parseFloat(metrics.porcentaje || 0) - parseFloat(workDayMetrics.percentage);
          return (
            <div className="working-days-bar">
              <div className="wd-item"><span>Días Hábiles:</span> <strong>{workDayMetrics.total}</strong></div>
              <div className="wd-item"><span>Transcurridos:</span> <strong>{workDayMetrics.elapsed}</strong></div>
              <div className="wd-item"><span>Avance:</span> <strong>{workDayMetrics.percentage}%</strong></div>
              <div className="wd-separator"></div>
              <div className={`wd-item variation-item ${timeVsStockDiff >= 0 ? 'positive' : 'negative'}`}>
                {timeVsStockDiff >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <strong>{Math.round(timeVsStockDiff)}%</strong>
                <span className="wd-label">(vs Avance)</span>
              </div>
            </div>
          );
        })()}

        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros de Inventario</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters} title="Restablecer filtros">
                <FilterX size={16} />
              </button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters).map((key) => (
              <MultiSelectFilter 
                key={key} 
                label={filterLabels[key] || key} 
                name={key} 
                icon={Tag} 
                options={availableOptions[key] || []}
                selectedValues={filters[key] || []}
                onChange={(name, vals) => {
                  console.log(`🔄 Cambiando filtro ${name}:`, vals);
                  setFilters(prev => ({ ...prev, [name]: vals }));
                }} 
                onClearPill={(name) => handleClearPill(name)}
              />
            ))}
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters}>
              <FilterX size={12} className="pill-icon" />
              <span className="pill-label">Limpiar</span>
            </button>
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        <div className="dashboard-indicators2">
          <div className="indicator-card2 v-real">
            <div className="card-content">
              <div className="inven-icon-wrapper">
                <SquareCheckBig size={22} color="white" />
                <span className={`daily-variation ${getDailyVarClass(metrics.var_diaria_disp)}`}>
                  {getDailyIcon(metrics.var_diaria_disp)}
                  {getDailyText(metrics.var_diaria_disp)}
                  <span>Var% Diaria</span>
                </span>
              </div>
              <span className="indicator-label">Stock Disponible ({unitMode === 'bultos' ? 'BL' : 'U'})</span>
              <div className="indicator-right-group">
                <span className="indicator-value">{formatNumber(metrics.stock_disponible)}</span>
              </div>
            </div>
          </div>
          
          <div className="indicator-card2 v-real">
            <div className="card-content">
              <div className="inven-icon-wrapper">
                <div className="icon-with-var">
                  <ArrowUpNarrowWide size={22} color="white" />
                  <span className={`daily-variation ${getDailyVarClass(metrics.var_diaria_demanda)}`}>
                    {getDailyIcon(metrics.var_diaria_demanda)}
                    {getDailyText(metrics.var_diaria_demanda)}
                    <span>Var% Diaria</span>
                  </span>
                </div>
              </div>
              <span className="indicator-label">Demanda ({unitMode === 'bultos' ? 'Bultos' : 'Unidades'})</span>
              <div className="indicator-right-group">
                <span className="indicator-value">{formatNumber(metrics.demanda)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="animated-divider" />

        <div className="inventory-nav-bar">
          <button 
            className={`nav-btn nav-detalle ${activeNavTab === 'detalle' ? 'active' : ''}`}
            onClick={() => handleNavTabChange('detalle')}
          >
            <List size={18} />
            <span>Detalle</span>
          </button>
          <button 
            className={`nav-btn nav-analisis ${activeNavTab === 'analisis' ? 'active' : ''}`}
            onClick={() => handleNavTabChange('analisis')}
          >
            <BarChart3 size={18} />
            <span>Análisis</span>
          </button>
          <button 
            className={`nav-btn nav-analisis ${activeNavTab === 'disp' ? 'active' : ''}`}
            onClick={() => handleNavTabChange('disp')}
          >
            <ListChecks size={18} />
            <span>Disponibilidad</span>
          </button>
        </div>

        {activeNavTab === 'detalle' && (
          <div className="detail-table-container">
            <div className="table-header">
              <h3>Detalle de Inventario</h3>
              <div className="table-controls">
                <button
                  className="export-table-btn"
                  onClick={() => downloadDetailExcel(detailData, unitMode)}
                  disabled={detailLoading || detailData.length === 0}
                >
                  <Download size={18} />
                </button>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    className={`switch-toggle-btn ${detailViewMode === 'SIMPLIFICADO' ? 'active' : ''}`}
                    style={{ padding: '6px 10px' }}
                    onClick={() => setDetailViewMode('SIMPLIFICADO')}
                  >
                    Simplificado
                  </button>
                  <button
                    className={`switch-toggle-btn ${detailViewMode === 'TOTAL' ? 'active' : ''}`}
                    style={{ padding: '6px 10px' }}
                    onClick={() => setDetailViewMode('TOTAL')}
                  >
                    Total
                  </button>
                </div>
                <span>Total registros: {detailData.length}</span>

                <div className="pagination-controls">
                  <button 
                    onClick={() => fetchDetailData(Math.max(1, detailPage - 1))}
                    disabled={detailPage === 1 || detailLoading}
                  >
                    Anterior
                  </button>
                  <span>
                    Página {detailPage} de {detailTotalPages}
                  </span>
                  <button 
                    onClick={() => fetchDetailData(detailPage + 1)}
                    disabled={detailPage >= detailTotalPages || detailLoading}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
            
            {detailLoading ? (
              <div className="loading-table">
                <RefreshCw className="spin-anim" size={32} />
                <span>Cargando detalle...</span>
              </div>
            ) : detailData.length === 0 ? (
              <div className="empty-state">
                <Archive size={48} />
                <h4>No hay datos de detalle</h4>
                <p>Ajusta los filtros para ver el detalle del inventario</p>
              </div>
            ) : (
              <div className="responsive-table-wrapper">
                <table className="detail-table">
                  <thead>
                    <tr>
                      {detailViewMode === 'SIMPLIFICADO' ? (
                        <>
                          <th>Cod Almacén</th>
                          <th>Sku ID</th>
                          <th>Producto</th>
                          <th>Stock Disp.</th>
                        </>
                      ) : (
                        <>
                          <th>Cod Almacén</th>
                          <th>Almacén</th>
                          <th>Proveedor</th>
                          <th>Marca</th>
                          <th>Línea</th>
                          <th>Sku ID</th>
                          <th>Producto</th>
                          <th>Clasif.</th>
                          <th>Stock Disp.</th>
                          <th>Venta Día</th>
                          <th>Fecha</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.map((row, index) => {
                      const suffix = unitMode === 'bultos' ? '_BL' : '_U';
                      const stockDisp = row.STOCK_DISPONIBLE ?? row[`STOCK_DISP${suffix}`] ?? 0;
                      const ventaDia = row.VENTA_DIA ?? row[`VENTA_DIA${suffix}`] ?? 0;
                      const isSimplificado = detailViewMode === 'SIMPLIFICADO';

                      return (
                        <tr key={`detail-row-${index}`}>
                          {isSimplificado ? (
                            <>
                              <td data-label="Cod Almacén">{row.CO_ALMA || '-'}</td>
                              <td data-label="Sku ID">{row.ID_ARTICULO || '-'}</td>
                              <td data-label="Producto">{row.IN_DESC1 || '-'}</td>
                              <td data-label="Stock Disp." className="number positive">
                                {formatNumber(stockDisp)}
                              </td>
                            </>
                          ) : (
                            <>
                              <td data-label="Cod Almacén">{row.CO_ALMA || '-'}</td>
                              <td data-label="Almacén">{row.DES_ALMACEN || '-'}</td>
                              <td data-label="Proveedor">{row.IN_PROVEEDOR || '-'}</td>
                              <td data-label="Marca">{row.IN_MARCA || '-'}</td>
                              <td data-label="Línea">{row.IN_LINEA || '-'}</td>
                              <td data-label="Sku ID">{row.ID_ARTICULO || '-'}</td>
                              <td data-label="Producto">{row.IN_DESC1 || '-'}</td>
                              <td data-label="Clasif.">{row.IN_CLASIF2 || '-'}</td>
                              <td data-label="Stock Disp." className="number positive">
                                {formatNumber(stockDisp)}
                              </td>
                              <td data-label="Venta Día" className="number">
                                {formatNumber(ventaDia)}
                              </td>
                              <td data-label="Fecha">{row.IN_FECHA || '-'}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {activeNavTab === 'analisis' && (
          <div className="detail-table-container">
            <div className="table-header">
              <h3>Análisis Meta vs Stock (Mensual)</h3>
              <div className="table-controls">
                <button 
                  className="export-table-btn"
                  onClick={() => downloadAnalysisExcel(analysisData)}
                  disabled={analysisLoading || analysisData.length === 0}
                >
                  <Download size={18} />
                </button>

                <button
                  className={`switch-toggle-btn ${analysisShowOnlyAlertEmergency ? 'active' : ''}`}
                  style={{ padding: '6px 10px' }}
                  onClick={() => setAnalysisShowOnlyAlertEmergency(v => !v)}
                >
                  Solo Alerta/Emergencia
                </button>

                <span>Marcas: {analysisData.length}</span>
              </div>
            </div>

            {analysisLoading ? (
              <div className="loading-table">
                <RefreshCw className="spin-anim" size={32} />
                <span>Cargando análisis...</span>
              </div>
            ) : (
              <div className="responsive-table-wrapper">
                <table className="detail-table analysis-table analysis-mobile-fix">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}></th>
                      <th>Marca / Producto</th>
                      <th className="number">{comparisonMode}</th>
                      <th className="number">Real</th>
                      <th className="number">% Cumpl.</th>
                      <th className="number">Stock Disp.</th>
                      <th className="number">Días Inv.</th>
                      <th className="number">Necesidad</th>
                      <th className="number">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysisData.map((brand, bIdx) => {
                      const isExpanded = expandedAnalysisBrands[brand.marca];
                      const compl = brand.cuota_forecast > 0 ? (brand.real_venta / brand.cuota_forecast * 100) : 0;
                      const diasInvBrand = brand.venta_diaria_3m > 0 ? (brand.inventario / brand.venta_diaria_3m) : 999;
                      
                      const brandStatus = getStockStatus(brand.inventario, brand.necesidad);

                      const showBrand = !analysisShowOnlyAlertEmergency
                        ? true
                        : ['Alerta', 'Emergencia'].includes(brandStatus.label) || (brand.productos || []).some((prod) => {
                            const prodStatus = getStockStatus(prod.inventario, prod.necesidad);
                            return ['Alerta', 'Emergencia'].includes(prodStatus.label);
                          });

                      if (!showBrand) return null;

                      return (
                        <React.Fragment key={`brand-${bIdx}`}>
                          <tr className="parent-row pointer" onClick={() => toggleAnalysisBrand(brand.marca)}>
                            <td>{isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                            <td className="bold">{brand.marca}</td>
                            <td className="number">{formatNumber(brand.cuota_forecast)}</td>
                            <td className="number">{formatNumber(brand.real_venta)}</td>
                            <td className="number bold">
                              <div className="status-with-dot">
                                <span>{compl.toFixed(0)}%</span>
                                <div className="status-dot" style={{ backgroundColor: getSemaforoColor(compl), margin: 0 }}></div>
                              </div>
                            </td>
                            <td className="number">{formatNumber(brand.inventario)}</td>
                            <td className="number bold" style={{ color: diasInvBrand < 15 ? '#990000' : '#187900' }}>
                              {diasInvBrand >= 999 ? '>365 días' : `${diasInvBrand.toFixed(1)} días`}
                            </td>
                            <td className="number orange">{formatNumber(brand.necesidad)}</td>
                            <td className="status-cell">
                              <div className="status-tag" style={{ color: brandStatus.color }}>
                                {brandStatus.icon}
                                <span>{brandStatus.label}</span>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && brand.productos
                            .filter((prod) => {
                              if (!analysisShowOnlyAlertEmergency) return true;
                              const prodStatus = getStockStatus(prod.inventario, prod.necesidad);
                              return ['Alerta', 'Emergencia'].includes(prodStatus.label);
                            })
                            .map((prod, pIdx) => {
                              const prodStatus = getStockStatus(prod.inventario, prod.necesidad);
                              const dInv = Number(prod.dias_inventario || 0);
                              return (
                                <tr key={`prod-${bIdx}-${pIdx}`} className="child-row">
                                  <td></td>
                                  <td className="pl-30 truncate" title={prod.producto}>{prod.id_articulo} - {prod.producto}</td>
                                  <td className="number">{formatNumber(prod.cuota_forecast)}</td>
                                  <td className="number">{formatNumber(prod.real_venta)}</td>
                                  <td className="number">
                                    <div className="status-with-dot">
                                      <span>{prod.cumplimiento_pct.toFixed(0)}%</span>
                                      <div className="status-dot sm" style={{ backgroundColor: getSemaforoColor(prod.cumplimiento_pct), margin: 0 }}></div>
                                    </div>
                                  </td>
                                  <td className="number">{formatNumber(prod.inventario)}</td>
                                  <td className="number" style={{ fontSize: '0.75rem', color: dInv < 15 ? '#8a0000' : '#187900' }}>
                                    {dInv >= 999 ? '>365 días' : `${dInv.toFixed(1)} días`}
                                  </td>
                                  <td className="number orange">{formatNumber(prod.necesidad)}</td>
                                  <td className="status-cell">
                                    <div className="status-tag" style={{ color: prodStatus.color }}>
                                      {prodStatus.icon}
                                      <span>{prodStatus.label}</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}

                    {(() => {
                      const totals = analysisData.reduce((acc, brand) => {
                        acc.cuota_forecast += brand.cuota_forecast || 0;
                        acc.real_venta += brand.real_venta || 0;
                        acc.inventario += brand.inventario || 0;
                        acc.necesidad += brand.necesidad || 0;
                        acc.venta_diaria_3m += brand.venta_diaria_3m || 0;
                        return acc;
                      }, {
                        cuota_forecast: 0,
                        real_venta: 0,
                        inventario: 0,
                        necesidad: 0,
                        venta_diaria_3m: 0
                      });
                      
                      const totalCumpl = totals.cuota_forecast > 0
                        ? (totals.real_venta / totals.cuota_forecast * 100)
                        : 0;

                      const totalDias = totals.venta_diaria_3m > 0 ? (totals.inventario / totals.venta_diaria_3m) : 999;

                      const totalsStatus = getStockStatus(totals.inventario, totals.necesidad);
                      return (
                        <tr className="analysis-totals-row">
                          <td></td>
                          <td className="bold">TOTAL</td>
                          <td className="number">{formatNumber(totals.cuota_forecast)}</td>
                          <td className="number">{formatNumber(totals.real_venta)}</td>
                          <td className="number bold">
                            <div className="status-with-dot">
                              <span>{totalCumpl.toFixed(0)}%</span>
                              <div className="status-dot" style={{ backgroundColor: getSemaforoColor(totalCumpl), margin: 0 }}></div>
                            </div>
                          </td>
                          <td className="number">{formatNumber(totals.inventario)}</td>
                          <td className="number bold" style={{ color: totalDias < 15 ? '#ff4d4d' : 'inherit' }}>
                            {totalDias >= 999 ? '>365 días' : `${totalDias.toFixed(1)} días`}
                          </td>
                          <td className="number orange">{formatNumber(totals.necesidad)}</td>
                          <td className="status-cell">
                            <div className="status-tag" style={{ color: totalsStatus.color }}>
                              {totalsStatus.icon}
                              <span>{totalsStatus.label}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      
        {activeNavTab === 'disp' && (
          <div className="detail-table-container">
            <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '12px', paddingBottom: '8px', flexWrap: 'wrap' }}>
              <h3>Matriz de Disponibilidad {dispViewMode === 'SEMANAL' ? 'Semanal' : 'Mensual'}</h3>
              
              <div className="table-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                {/* Toggle Mensual/Semanal */}
                <div className="switch-toggle-group">
                  <button 
                    className={`switch-toggle-btn ${dispViewMode === 'MENSUAL' ? 'active' : ''}`}
                    style={{ padding: '6px 12px' }}
                    onClick={() => handleDispViewModeChange('MENSUAL')}
                  >
                    Mensual
                  </button>
                  <button 
                    className={`switch-toggle-btn ${dispViewMode === 'SEMANAL' ? 'active' : ''}`}
                    style={{ padding: '6px 12px' }}
                    onClick={() => handleDispViewModeChange('SEMANAL')}
                  >
                    Semanal
                  </button>
                </div>
                
                {/* Filtro de meses SOLO en vista MENSUAL */}
                {dispViewMode === 'MENSUAL' && (
                  <MultiSelectFilter 
                    label="Meses Disp." 
                    name="dispMeses" 
                    icon={Calendar} 
                    options={nombresMeses.map(m => ({ value: m, label: m.charAt(0) + m.slice(1).toLowerCase() }))}
                    selectedValues={dispSelectedMeses} 
                    onChange={(name, vals) => {
                      console.log('📅 Meses disponibilidad seleccionados:', vals);
                      setDispSelectedMeses(vals);
                    }} 
                  />
                )}
                
                {/* En vista SEMANAL, mostrar el mes seleccionado globalmente */}
                {dispViewMode === 'SEMANAL' && selectedMeses.length > 0 && (
                  <span style={{ fontSize: '0.7rem', color: 'white', borderRadius: '4px' }}>
                    Mes: {selectedMeses[0].charAt(0) + selectedMeses[0].slice(1).toLowerCase()}
                  </span>
                )}
              </div>
            </div>

            {disponibilidadLoading ? (
              <div className="loading-table">
                <RefreshCw className="spin-anim" size={32} />
                <span>Cargando disponibilidad {dispViewMode === 'SEMANAL' ? 'semanal' : 'mensual'}...</span>
              </div>
            ) : disponibilidadData.length === 0 ? (
              <div className="empty-state">
                <Archive size={48} />
                <h4>No hay datos disponibles</h4>
                <p>Ajusta los filtros para visualizar información</p>
              </div>
            ) : (
              (() => {
                const columnas = Object.keys(disponibilidadData[0] || {}).filter(
                  col => col.endsWith('_Disp') || col.endsWith('_Quiebre')
                );
                
                const periodos = Array.from(
                  new Set(
                    columnas.map(col => col.split('_')[0])
                  )
                );
                
                const marcasMap = disponibilidadData.reduce((acc, row) => {
                  const marca = row.marca || 'SIN MARCA';
                  if (!acc[marca]) {
                    acc[marca] = {
                      marca: marca,
                      productos: []
                    };
                  }
                  if (row.id_articulo) {
                    acc[marca].productos.push(row);
                  }
                  return acc;
                }, {});
                
                const marcasAgrupadas = Object.values(marcasMap);
                
                return (
                  <div className="responsive-table-wrapper" style={{ overflow: 'auto', maxHeight: '600px' }}>
                    <table className="detail-table analysis-table analysis-mobile-fix">
                      <thead>
                        <tr>
                          <th rowSpan={2} style={{ position: 'sticky', top: 0, left: 0, zIndex: 5, width: '40px' }}></th>
                          <th rowSpan={2} style={{ position: 'sticky', top: 0, left: '40px', zIndex: 5, minWidth: '250px', textAlign: 'left' }}>
                            Marca / Producto
                          </th>
                          {periodos.map(periodo => (
                            <th key={`h1-${periodo}`} colSpan={2} style={{ position: 'sticky', top: 0, zIndex: 3, textAlign: 'center' }}>
                              {dispViewMode === 'SEMANAL' ? `Semana ${periodo.replace('S', '')}` : periodo.charAt(0) + periodo.slice(1).toLowerCase()}
                            </th>
                          ))}
                        </tr>
                        <tr>
                          {periodos.map(periodo => (
                            <React.Fragment key={`h2-${periodo}`}>
                              <th className="number" style={{ position: 'sticky', top: '52px', zIndex: 3, fontSize: '0.8rem', fontWeight: '600' }}>% Disp.</th>
                              <th className="number" style={{ position: 'sticky', top: '52px', zIndex: 3, fontSize: '0.8rem', fontWeight: '600' }}>% Quiebre</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {marcasAgrupadas.map((brand, bIdx) => {
                          const isExpanded = expandedAnalysisBrands[brand.marca];

                          return (
                            <React.Fragment key={`disp-brand-${bIdx}`}>
                              <tr className="parent-row pointer" onClick={() => toggleAnalysisBrand(brand.marca)}>
                                <td style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--in-table)', color: 'white' }}>
                                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </td>
                                <td className="bold" style={{ position: 'sticky', left: '40px', zIndex: 2, textAlign: 'left', background: 'var(--in-table)', color: 'white' }}>
                                  {brand.marca}
                                </td>
                                
                                {periodos.map(periodo => {
                                  const validDisps = brand.productos.map(p => p[`${periodo}_Disp`]).filter(v => v !== null && v !== undefined);
                                  const validQbrs = brand.productos.map(p => p[`${periodo}_Quiebre`]).filter(v => v !== null && v !== undefined);
                                  
                                  const avgDisp = validDisps.length ? (validDisps.reduce((s, v) => s + Number(v), 0) / validDisps.length) : 0;
                                  const avgQbr = validQbrs.length ? (validQbrs.reduce((s, v) => s + Number(v), 0) / validQbrs.length) : 0;

                                  return (
                                    <React.Fragment key={`brand-cells-${periodo}`}>
                                      <td className="number bold" style={{ color: avgDisp >= 95 ? '#187900' : avgDisp >= 80 ? '#eeff00' : '#990000' }}>
                                        {avgDisp.toFixed(1)}%
                                      </td>
                                      <td className="number bold" style={{ color: avgQbr <= 5 ? '#187900' : avgQbr <= 20 ? '#eeff00' : '#990000' }}>
                                        {avgQbr.toFixed(1)}%
                                      </td>
                                    </React.Fragment>
                                  );
                                })}
                              </tr>

                              {isExpanded && brand.productos.map((prod, pIdx) => (
                                <tr key={`disp-prod-${bIdx}-${pIdx}`} className="child-row">
                                  <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--in-table)', color: 'white' }}></td>
                                  <td className="pl-30 truncate" title={prod.producto} style={{ position: 'sticky', left: '40px', zIndex: 1, textAlign: 'left', background: 'var(--in-table)', color: 'white' }}>
                                    {prod.id_articulo} - {prod.producto}
                                  </td>
                                  
                                  {periodos.map(periodo => {
                                    const valDisp = Number(prod[`${periodo}_Disp`] || 0);
                                    const valQbr = Number(prod[`${periodo}_Quiebre`] || 0);

                                    return (
                                      <React.Fragment key={`prod-cells-${periodo}`}>
                                        <td className="number" style={{ color: valDisp >= 95 ? '#135f00' : valDisp >= 80 ? '#eeff00' : '#9a0000' }}>
                                          {valDisp.toFixed(1)}%
                                        </td>
                                        <td className="number" style={{ color: valQbr <= 5 ? '#187900' : valQbr <= 20 ? '#eeff00' : '#9a0000' }}>
                                          {valQbr.toFixed(1)}%
                                        </td>
                                      </React.Fragment>
                                    );
                                  })}
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </div>
        )}

        <footer className="status-footer">
          {querying ? "🔄 SINCRONIZANDO INVENTARIO..." : 
          `📊 INVENTARIO: ${selectedMeses.join(', ')} ${selectedAnios.join(', ')} Día: ${selectedDay.join(', ')}`}
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default DashboardInventario;